import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/_core/hooks/useAuth";
import { isMarketingRole } from "@shared/userRoles";
import { ClipboardList } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Redirect, useLocation } from "wouter";
import {
  DashboardRoleToggle,
  type DashboardViewRole,
} from "./DashboardRoleToggle";
import { SalesServiceDashboardView } from "./SalesServiceDashboardView";
import { ServiceDashboardGlobalSearch } from "./ServiceDashboardGlobalSearch";
import { TechnicianDashboardView } from "./TechnicianDashboardView";

const STORAGE_KEY = "service-dashboard-view-role";

function defaultViewForRole(role: string | undefined): DashboardViewRole {
  if (role === "sales") return "sales";
  if (role === "admin" || role === "finance") return "technician";
  if (["service", "installer", "programming"].includes(role ?? "")) return "technician";
  return "technician";
}

export function ServiceDashboardPage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const isAdmin = user?.role === "admin";
  const isMarketing = Boolean(user && isMarketingRole(user.role));

  const defaultView = useMemo(() => defaultViewForRole(user?.role), [user?.role]);

  const [viewRole, setViewRole] = useState<DashboardViewRole>(() => {
    if (typeof window === "undefined") return defaultView;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "technician" || stored === "sales") return stored;
    return defaultView;
  });

  useEffect(() => {
    if (!isAdmin) setViewRole(defaultView);
  }, [defaultView, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      window.localStorage.setItem(STORAGE_KEY, viewRole);
    }
  }, [viewRole, isAdmin]);

  if (isMarketing) {
    return <Redirect to="/admin/articles" />;
  }

  const greeting = user?.name?.split(" ")[0] ?? "there";
  const isSalesView = viewRole === "sales";

  return (
    <DashboardLayout>
      <div className="min-h-full bg-slate-50/40 -m-4 sm:-m-6 p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-[1680px] space-y-6">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                Good {getTimeOfDay()}, {greeting}
              </h1>
              <p className="mt-1 text-sm text-slate-500 max-w-lg">
                {isSalesView
                  ? "Here's an overview of your customers and service activity."
                  : "What Service is doing now, what is coming next, and what needs attention."}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {isAdmin ? (
                <DashboardRoleToggle value={viewRole} onChange={setViewRole} />
              ) : null}
              <Button
                size="sm"
                variant="outline"
                className="border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                onClick={() => setLocation("/repair-orders/new")}
              >
                <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                Create RO
              </Button>
            </div>
          </header>

          <ServiceDashboardGlobalSearch />

          {isSalesView ? <SalesServiceDashboardView /> : <TechnicianDashboardView />}
        </div>
      </div>
    </DashboardLayout>
  );
}

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}
